import { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import { workflowOptionLookup } from '../../../shared/config/workflowUi';
import type { Option } from '../../../shared/types';
import type {
  WorkflowSummaryCard,
} from '../../../shared/types/mappingWorkspace.types';

const CONNECTOR_SUMMARY_FIELDS = [
  'endpoint1Value',
  'endpoint2Value',
  'endpoint3Value',
  'endpoint4Value',
  'materialValue',
  'designStdValue',
  'pressureClassValue',
  'insulationSpecValue',
  'insulationThicknessValue',
  'tracingSpecValue',
  'itemDescriptionValue',
] as const;

const BOLT_CORE_FIELDS = [
  'boltDiaValue',
  'boltQuantityValue',
  'boltLengthValue',
] as const;

const BOLT_GASKET_FIELDS = [
  'boltSetActiveValue',
  'boltItemDescriptionValue',
  'boltDiaValue',
  'boltQuantityValue',
  'boltLengthValue',
  'boltMaterialValue',
  'boltStandardValue',
] as const;

const ISO_CATEGORY_FIELDS = [
  'valveTagValue',
  'supportsTagValue',
  'supportsSsrefValue',
  'instrumentTagValue',
] as const;

export type { SummaryTone, WorkflowSummaryCard } from '../../../shared/types/mappingWorkspace.types';

export interface MappingWorkspaceGuideDefinition {
  id: number;
  title: string;
  summary: string;
  hint: string;
  status?: string | null;
  recommended?: boolean;
  iconId: 'mapping' | 'quickFill' | 'validation' | 'export';
}

interface BuildMappingWorkspaceCompletionModelInput {
  workflowSetupCompleted: boolean;
  selectedOption: Option;
  selectedSettingsType: SettingsType;
  selectedProfileKey: string;
  scopeCount: number;
  workspaceReady: boolean;
  workspaceSummary: {
    requiredOpen: number;
    mapped: number;
    total: number;
  };
  connectorConfig: unknown;
  boltConfig: unknown;
  categoryConfig: unknown;
  processConfig: {
    processConditionsChecked: boolean;
    projectPropertiesChecked: boolean;
  };
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const readFieldValue = (field: unknown): string => {
  if (typeof field === 'string') {
    return field.trim();
  }

  if (typeof field === 'object' && field !== null && 'value' in field) {
    const value = (field as { value?: unknown }).value;
    return typeof value === 'string' ? value.trim() : '';
  }

  return '';
};

const countPopulatedFields = (config: unknown, fieldKeys: readonly string[]): number => {
  const record = asRecord(config);

  return fieldKeys.reduce(
    (total, fieldKey) => total + (readFieldValue(record[fieldKey]) !== '' ? 1 : 0),
    0
  );
};

export const buildMappingWorkspaceCompletionModel = ({
  workflowSetupCompleted,
  selectedOption,
  selectedSettingsType,
  selectedProfileKey,
  scopeCount,
  workspaceReady,
  workspaceSummary,
  connectorConfig,
  boltConfig,
  categoryConfig,
  processConfig,
}: BuildMappingWorkspaceCompletionModelInput) => {
  const workflowMeta = selectedOption ? workflowOptionLookup[selectedOption] : null;
  const showCompletedWorkflowChrome = workflowSetupCompleted;
  const selectedProfileName = selectedProfileKey ? selectedProfileKey.split('::')[0] : 'Current settings are not saved to a profile';
  const connectorFieldCount = countPopulatedFields(connectorConfig, CONNECTOR_SUMMARY_FIELDS);
  const isBoltBasedFlow = readFieldValue(asRecord(boltConfig).boltModelingChoice) === 'yes';
  const boltFieldKeys = isBoltBasedFlow ? BOLT_CORE_FIELDS : BOLT_GASKET_FIELDS;
  const boltFieldCount = countPopulatedFields(boltConfig, boltFieldKeys);
  const categoryFieldCount = countPopulatedFields(categoryConfig, ISO_CATEGORY_FIELDS);

  const workflowSummaryChips = [
    `Workflow source: ${workflowMeta?.shortLabel ?? 'Saved mapping profile'}`,
    'Handoff: mapping workspace',
    `Active profile: ${selectedProfileName}`,
    `Profile owner: ${selectedSettingsType === SettingsType.Company ? 'Company' : 'User'}`,
    `Mapping scopes: ${scopeCount}`,
    workspaceReady ? 'Handoff: ready for PCF Validation' : `Handoff blocked: ${workspaceSummary.requiredOpen} required attributes open`,
  ];

  const workflowSummaryCards: WorkflowSummaryCard[] = [
    {
      label: 'Connector profile evidence',
      value: `${connectorFieldCount} of ${CONNECTOR_SUMMARY_FIELDS.length} fields mapped`,
      detail:
        'The selected profile contains the endpoint, BOM, and component identity mappings configured during Guided Setup. Review any unmapped fields before PCF Validation.',
      tone: connectorFieldCount === CONNECTOR_SUMMARY_FIELDS.length ? 'success' : 'info',
    },
    {
      label: 'Bolt export evidence',
      value: isBoltBasedFlow ? 'Profile uses source BOLT data' : 'Profile derives standalone BOLT records from gasket data',
      detail:
        `${boltFieldCount} of ${boltFieldKeys.length} required bolt inputs are mapped for the profile's PCF export strategy. Any missing required input can result in incomplete bolt data.`,
      tone: boltFieldCount === boltFieldKeys.length ? 'success' : 'warning',
    },
    {
      label: selectedOption === 'Iso Drawings Generation' ? 'ISO category evidence' : 'ISO category scope',
      value:
        selectedOption === 'Iso Drawings Generation'
          ? `${categoryFieldCount} of ${ISO_CATEGORY_FIELDS.length} tag fields mapped`
          : 'ISO-only category tags are outside this workflow',
      detail:
        selectedOption === 'Iso Drawings Generation'
          ? 'The selected profile contains the valve, support, and instrument identifiers used for ISO output. Review any missing tags before PCF export.'
          : 'The selected workflow does not require ISO-only category tags, so their absence does not block this handoff.',
      tone:
        selectedOption === 'Iso Drawings Generation'
          ? categoryFieldCount > 0
            ? 'info'
            : 'default'
          : 'default',
    },
    {
      label: 'Process and project metadata',
      value: `${processConfig.processConditionsChecked ? 'Process included' : 'Process excluded'} · ${
        processConfig.projectPropertiesChecked ? 'Project included' : 'Project excluded'
      }`,
      detail:
        processConfig.processConditionsChecked || processConfig.projectPropertiesChecked
          ? 'The selected profile includes optional operating, compliance, or project metadata. Confirm those mappings before downstream review of the PCF output.'
          : 'The selected profile excludes optional process and project metadata; only the core PCF mapping will be handed off.',
      tone:
        processConfig.processConditionsChecked || processConfig.projectPropertiesChecked
          ? 'info'
          : 'default',
    },
    {
      label: 'Mapping handoff status',
      value: `${workspaceSummary.mapped} of ${workspaceSummary.total} PCF attributes mapped`,
      detail: workspaceReady
        ? 'Every required PCF attribute is mapped. The project is ready to proceed to PCF Validation before export.'
        : `${workspaceSummary.requiredOpen} required PCF attributes remain open. Resolve them in Mapping before PCF Validation or export.`,
      tone: workspaceReady ? 'success' : 'warning',
    },
  ];

  const workspaceGuides: MappingWorkspaceGuideDefinition[] = [
    {
      id: 0,
      title: 'Mapping',
      summary:
        'Use the active profile and mapping scope to define each PCF attribute before continuing to Quick Fill or Validation.',
      hint: workspaceReady
        ? 'Mapping is complete. Use Quick Fill only for repeated Revit data changes, then run PCF Validation to confirm project readiness.'
        : `Start here: ${workspaceSummary.requiredOpen} required PCF attributes are open and block validation handoff.`,
      status: workspaceReady ? 'Mapping complete' : `${workspaceSummary.requiredOpen} required open`,
      recommended: !workspaceReady,
      iconId: 'mapping',
    },
    {
      id: 1,
      title: 'Quick Fill',
      summary:
        'Apply the same Revit parameter correction across multiple Revit families.',
      hint:
        'Choose Quick Fill when the same missing or incorrect parameter affects multiple rows, then run PCF Validation to confirm the result.',
      status: 'Family parameter repair',
      recommended: false,
      iconId: 'quickFill',
    },
    {
      id: 2,
      title: 'PCF validation',
      summary:
        'Check the active Revit project against the selected profile and identify issues that could prevent a reliable PCF export.',
      hint:
        'Run PCF Validation after completing Mapping and any required Quick Fill corrections. Resolve all blocking issues before export.',
      status: 'Readiness check and repair',
      recommended: workspaceReady,
      iconId: 'validation',
    },
    {
      id: 3,
      title: 'PCF export',
      summary:
        'Review Revit piping systems and generate PCF files only after the selected profile has passed PCF Validation.',
      hint:
        'Choose PCF Export when Validation reports no blocking issues. The generated PCF files can then be used for downstream engineering workflows.',
      status: 'Engineering handoff',
      recommended: false,
      iconId: 'export',
    },
  ];

  return {
    showCompletedWorkflowChrome,
    workflowSummaryChips,
    workflowSummaryCards,
    workspaceGuides,
  };
};
